import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';

import { NumberOfBuyers, S3_FOLDER, states } from '@/config/constants';
import {
  useGetContractByAgentQuery,
  useUpdateContractAgentMutation,
} from '@/apis/extended/contractApi';
import { useGetSignedUrlMutation } from '@/apis/extended/globalApi';
import { getContractPropertyAddress } from '@/utils/contractPropertyAddress';
import { uploadStorageFile } from '@/utils/storage';
import { CommonActions } from '../../../Common/CommonContract/CommonActions';
import { CommonPrevNextButtons } from '../../../Common/CommonContract/CommonPrevNextButtons';
import { CommonFormControl } from '../../../Common/CommonFormControl';
import { CommonMultipleFileInput } from '../../../Common/CommonMultipleFileInput';

type FormType = {
  sellerName: string;
  numberOfSellers: number;
  sellerEmail: string;
  sellerPhone: string;
  seller2Name: string;
  seller2Email: string;
  seller2Phone: string;
  propertyAddress: string;
  propertyCity: string;
  propertyState: string;
  propertyZipCode: string;
  propertyCounty: string;
  buyerAgentCommission: number;
  sellerPOA: boolean;
  sellerLLC: boolean;
  legalDescription: string;
  zoning: string;
  associationType: string; // 'none' | 'hoa' | 'condo'
  associationName: string;
  sellerDisclosures: string[];
};

const initialForm: FormType = {
  sellerName: '',
  numberOfSellers: 1,
  sellerEmail: '',
  sellerPhone: '',
  seller2Name: '',
  seller2Email: '',
  seller2Phone: '',
  propertyAddress: '',
  propertyCity: '',
  propertyState: '',
  propertyZipCode: '',
  propertyCounty: '',
  buyerAgentCommission: 0,
  sellerPOA: false,
  sellerLLC: false,
  legalDescription: '',
  zoning: '',
  associationType: 'none',
  associationName: '',
  sellerDisclosures: [],
};

type ContractDetailAgentSellerInfoProps = {
  uid: string;
  onPrev: () => void;
  onNext: () => void;
  sellerName?: string;
  numberOfSellers?: number;
  sellerEmail?: string;
  sellerPhone?: string;
  seller2Name?: string;
  seller2Email?: string;
  seller2Phone?: string;
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZipCode?: string;
  propertyCounty?: string;
  buyerAgentCommission?: number;
  sellerPOA?: boolean;
  sellerLLC?: boolean;
  legalDescription?: string;
  zoning?: string;
  associationType?: string;
  associationName?: string;
  sellerDisclosures?: string[];
};

export const ContractDetailAgentSellerInfo = ({
  uid,
  onPrev,
  onNext,
}: ContractDetailAgentSellerInfoProps) => {
  const { data: contract } = useGetContractByAgentQuery(uid);
  const [updateContractAgent, { isLoading }] = useUpdateContractAgentMutation();
  const [getSignedUrl] = useGetSignedUrlMutation();

  const [form, setForm] = useState(initialForm);
  const [isUploading, setIsUploading] = useState(false);
  const [isAddressConfirmed, setIsAddressConfirmed] = useState(false);

  useEffect(() => {
    const agentSellerInfo = contract?.agentSellerInfo;
    if (!contract && !agentSellerInfo) return;

    const prop = getContractPropertyAddress(contract as any);

    setForm({
      sellerName: agentSellerInfo?.sellerName || '',
      numberOfSellers: agentSellerInfo?.numberOfSellers || 1,
      sellerEmail: agentSellerInfo?.sellerEmail || '',
      sellerPhone: agentSellerInfo?.sellerPhone || '',
      seller2Name: agentSellerInfo?.seller2Name || '',
      seller2Email: agentSellerInfo?.seller2Email || '',
      seller2Phone: agentSellerInfo?.seller2Phone || '',
      // Read-only: always from product database
      propertyAddress: prop.addressLine,
      propertyCity: prop.city,
      propertyState: prop.state,
      propertyZipCode: prop.zip,
      propertyCounty: agentSellerInfo?.propertyCounty || '', // County still requires manual input
      buyerAgentCommission: agentSellerInfo?.buyerAgentCommission || 0,
      sellerPOA: agentSellerInfo?.sellerPOA || false,
      sellerLLC: agentSellerInfo?.sellerLLC || false,
      legalDescription: agentSellerInfo?.legalDescription || '',
      zoning: agentSellerInfo?.zoning || '',
      associationType: agentSellerInfo?.associationType || 'none',
      associationName: agentSellerInfo?.associationName || '',
      sellerDisclosures: agentSellerInfo?.sellerDisclosures || [],
    });

    // If we already have agentSellerInfo saved, assume address was previously confirmed.
    setIsAddressConfirmed(!!agentSellerInfo?.isStored);
  }, [contract]);

  const isValid = useMemo(() => {
    return (
      form.sellerName &&
      form.numberOfSellers > 0 &&
      form.sellerEmail &&
      form.sellerPhone &&
      form.propertyAddress &&
      form.propertyCity &&
      form.propertyState &&
      form.propertyZipCode &&
      form.propertyCounty &&
      form.buyerAgentCommission &&
      form.legalDescription &&
      form.zoning &&
      form.associationType &&
      // If association type is hoa or condo, association name is required
      (form.associationType === 'none' || form.associationName) &&
      isAddressConfirmed
    );
  }, [form, isAddressConfirmed]);

  const isMissingProductAddress = useMemo(() => {
    // If addressLine is empty, we have nothing to confirm.
    return !form.propertyAddress;
  }, [form.propertyAddress]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.checked });
  };

  const handleAddSellerDisclosure = async (file: File | null) => {
    if (!file) return;
    let documentUrl;
    setIsUploading(true);
    try {
      documentUrl = await uploadStorageFile(file, S3_FOLDER.DISCLOSURE, () =>
        getSignedUrl({
          folderName: S3_FOLDER.DISCLOSURE,
          fileName: file.name,
        }).unwrap()
      );

      if (!documentUrl) return;

      setForm({
        ...form,
        sellerDisclosures: [...form.sellerDisclosures, documentUrl],
      });
    } catch (err) {
      console.error(err);
    }
    setIsUploading(false);
  };

  const handleRemoveSellerDisclosure = (url: string) => {
    setForm({
      ...form,
      sellerDisclosures: form.sellerDisclosures.filter((item) => item !== url),
    });
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    await updateContractAgent({
      uid,
      agentSellerInfo: {
        sellerName: form.sellerName,
        numberOfSellers: form.numberOfSellers,
        sellerEmail: form.sellerEmail,
        sellerPhone: form.sellerPhone,
        seller2Name: form.seller2Name,
        seller2Email: form.seller2Email,
        seller2Phone: form.seller2Phone,
        // Persisting these for completeness, but values come from product DB (read-only)
        propertyAddress: form.propertyAddress,
        propertyCity: form.propertyCity,
        propertyState: form.propertyState,
        propertyZipCode: form.propertyZipCode,
        propertyCounty: form.propertyCounty,
        buyerAgentCommission: form.buyerAgentCommission,
        sellerPOA: form.sellerPOA,
        sellerLLC: form.sellerLLC,
        legalDescription: form.legalDescription,
        zoning: form.zoning,
        associationType: form.associationType,
        associationName: form.associationName,
        sellerDisclosures: form.sellerDisclosures,
        isStored: true,
      },
    }).unwrap();
    onNext();
  };
  const handleSelectChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  return (
    <Box>
      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Seller Information"
          action={
            <CommonActions
              isLoading={isLoading}
              isNextDisabled={!isValid}
              onPrev={onPrev}
              onNext={handleSubmit}
            />
          }
          avatar={<i className="fa-light fa-user" />}
        />
        <CardContent>
          <CommonFormControl label="Seller Name:" labelWidth={150}>
            <TextField
              placeholder="Seller Name"
              name="sellerName"
              value={form.sellerName}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl
            label="Number of Sellers:"
            labelWidth={150}
            sx={{ mt: 2 }}
          >
            <Select
              placeholder="Number of Sellers"
              name="numberOfSellers"
              value={form.numberOfSellers.toString()}
              onChange={handleSelectChange}
            >
              {NumberOfBuyers.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </CommonFormControl>
          <CommonFormControl
            sx={{ mt: 2 }}
            label="Seller Email:"
            labelWidth={150}
          >
            <TextField
              placeholder="Seller Email"
              name="sellerEmail"
              value={form.sellerEmail}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl
            sx={{ mt: 2 }}
            label="Seller Phone:"
            labelWidth={150}
          >
            <TextField
              placeholder="Seller Phone"
              name="sellerPhone"
              value={form.sellerPhone}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl
            sx={{ mt: 2 }}
            label="Property Address:"
            labelWidth={150}
          >
            <TextField
              placeholder="Property Address (auto-filled from database)"
              name="propertyAddress"
              value={form.propertyAddress}
              onChange={handleInputChange}
              disabled
              helperText="Auto-populated from property listing"
              inputProps={{
                style:
                  form.propertyAddress &&
                  !contract?.agentSellerInfo?.propertyAddress
                    ? { backgroundColor: '#e8f5e9' }
                    : undefined,
              }}
            />
          </CommonFormControl>
          <CommonFormControl
            sx={{ mt: 2 }}
            label="Property City:"
            labelWidth={150}
          >
            <TextField
              placeholder="Property City (auto-filled from database)"
              name="propertyCity"
              value={form.propertyCity}
              onChange={handleInputChange}
              disabled
              helperText="Auto-populated from property listing"
              inputProps={{
                style:
                  form.propertyCity && !contract?.agentSellerInfo?.propertyCity
                    ? { backgroundColor: '#e8f5e9' }
                    : undefined,
              }}
            />
          </CommonFormControl>
          <CommonFormControl
            sx={{ mt: 2 }}
            label="Property State:"
            labelWidth={150}
          >
            <Select
              placeholder="Property State (auto-filled from database)"
              name="propertyState"
              value={form.propertyState}
              onChange={handleSelectChange}
              disabled
              style={
                form.propertyState && !contract?.agentSellerInfo?.propertyState
                  ? { backgroundColor: '#e8f5e9' }
                  : undefined
              }
            >
              {states.map((state) => (
                <MenuItem key={state.value} value={state.value}>
                  {state.label}
                </MenuItem>
              ))}
            </Select>
          </CommonFormControl>
          <CommonFormControl
            sx={{ mt: 2 }}
            label="Property Zip Code:"
            labelWidth={150}
          >
            <TextField
              placeholder="Property Zip Code (auto-filled from database)"
              name="propertyZipCode"
              value={form.propertyZipCode}
              onChange={handleInputChange}
              disabled
              helperText="Auto-populated from property listing"
              inputProps={{
                style:
                  form.propertyZipCode &&
                  !contract?.agentSellerInfo?.propertyZipCode
                    ? { backgroundColor: '#e8f5e9' }
                    : undefined,
              }}
            />
          </CommonFormControl>

          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              This address is pulled from the property record. Please confirm it
              matches the listing before proceeding.
            </Alert>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={isAddressConfirmed}
                  onChange={(e) => setIsAddressConfirmed(e.target.checked)}
                />
              }
              label="I confirm the property address above is correct."
            />
          </Box>
          <CommonFormControl
            sx={{ mt: 2 }}
            label="Property County:"
            labelWidth={150}
          >
            <TextField
              placeholder="Enter County (required)"
              name="propertyCounty"
              value={form.propertyCounty}
              onChange={handleInputChange}
              helperText="County must be entered manually"
              required
            />
          </CommonFormControl>
          <CommonFormControl
            sx={{ mt: 2 }}
            label="Buyer Agent Commission (%):"
            labelWidth={150}
          >
            <TextField
              type="number"
              placeholder="Buyer Agent Commission"
              name="buyerAgentCommission"
              value={form.buyerAgentCommission}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          {form.numberOfSellers === 2 && (
            <React.Fragment key="seller2-fields">
              <CommonFormControl
                sx={{ mt: 2 }}
                label="Seller Name 2:"
                labelWidth={150}
              >
                <TextField
                  placeholder="Seller Name 2 "
                  name="seller2Name"
                  value={form.seller2Name}
                  onChange={handleInputChange}
                />
              </CommonFormControl>
              <CommonFormControl
                sx={{ mt: 2 }}
                label="Seller Email 2:"
                labelWidth={150}
              >
                <TextField
                  placeholder="Seller Email 2 "
                  name="seller2Email"
                  value={form.seller2Email}
                  onChange={handleInputChange}
                />
              </CommonFormControl>
              <CommonFormControl
                sx={{ mt: 2 }}
                label="Seller Phone 2:"
                labelWidth={150}
              >
                <TextField
                  placeholder="Seller Phone 2 "
                  name="seller2Phone"
                  value={form.seller2Phone}
                  onChange={handleInputChange}
                />
              </CommonFormControl>
            </React.Fragment>
          )}
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.sellerPOA}
                  name="sellerPOA"
                  onChange={handleCheckboxChange}
                />
              }
              label="Seller POA"
            />
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.sellerLLC}
                  name="sellerLLC"
                  onChange={handleCheckboxChange}
                />
              }
              label="Seller LLC"
            />
          </Box>
          <CommonFormControl
            sx={{ mt: 2 }}
            label="Legal Description:"
            labelWidth={150}
          >
            <TextField
              fullWidth
              placeholder="Legal Description"
              name="legalDescription"
              value={form.legalDescription}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl sx={{ mt: 2 }} label="Zoning:" labelWidth={150}>
            <TextField
              fullWidth
              placeholder="Zoning"
              name="zoning"
              value={form.zoning}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl
            label="Association Type:"
            labelWidth={150}
            sx={{ mt: 2 }}
          >
            <Select
              placeholder="Association Type"
              name="associationType"
              value={form.associationType}
              onChange={handleSelectChange}
            >
              <MenuItem value="none">No Homeowners Association</MenuItem>
              <MenuItem value="hoa">Homeowners Association</MenuItem>
              <MenuItem value="condo">Condo Association</MenuItem>
            </Select>
          </CommonFormControl>
          {(form.associationType === 'hoa' ||
            form.associationType === 'condo') && (
            <CommonFormControl
              sx={{ mt: 2 }}
              label="Association Name:"
              labelWidth={150}
            >
              <TextField
                fullWidth
                placeholder="Enter Association Name"
                name="associationName"
                value={form.associationName}
                onChange={handleInputChange}
                required
              />
            </CommonFormControl>
          )}
          <CommonFormControl
            label="Seller Disclosures:"
            labelWidth={150}
            sx={{ mt: 1 }}
          >
            <CommonMultipleFileInput
              urls={form.sellerDisclosures}
              isLoading={isUploading}
              onFileChange={handleAddSellerDisclosure}
              label="Add Disclosure Document"
              onRemove={handleRemoveSellerDisclosure}
            />
          </CommonFormControl>
        </CardContent>
      </Card>
      <CommonPrevNextButtons
        sx={{ mt: 3 }}
        isLoading={isLoading}
        isNextDisabled={!isValid}
        onPrev={onPrev}
        onNext={handleSubmit}
      />
    </Box>
  );
};
